import { DollarSign } from 'lucide-react'
import { AnimatedKpiValue } from '@/components/ui/AnimatedKpiValue'
import { OPERACAO_METRIC_ACCENTS } from '@/components/Dashboard/OperacaoMetricCard'
import type { JourneyMacroAggregate } from '@/components/Dashboard/operacaoTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'
import { formatCurrencyCompact } from '@/utils/format-currency'

type Props = {
  pipelineValue: number
  pipelineCount: number
  journeySteps: JourneyMacroAggregate[]
  valuesVisible?: boolean
  animationDelay?: number
}

function avgTicket(value: number, count: number): number {
  if (count <= 0) return 0
  return value / count
}

export function OperacaoPipelineHeroCard({
  pipelineValue,
  pipelineCount,
  journeySteps,
  valuesVisible = false,
  animationDelay = 0,
}: Props) {
  const accent = OPERACAO_METRIC_ACCENTS.pipeline
  const useCompact = valuesVisible && pipelineValue >= 100_000
  const ticket = avgTicket(pipelineValue, pipelineCount)
  const stages = [...journeySteps]
    .filter((step) => step.count > 0)
    .sort((a, b) => b.count - a.count)
  const funnelTotal = stages.reduce((sum, step) => sum + step.count, 0)
  const outsideFunnel = Math.max(0, pipelineCount - funnelTotal)

  return (
    <article className='relative flex h-full min-h-[220px] flex-col overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm sm:p-6'>
      <div
        className='pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-40 blur-2xl'
        style={{ backgroundColor: accent.ring }}
        aria-hidden
      />

      <div className='flex items-start justify-between gap-4'>
        <div className='min-w-0'>
          <p className='text-sm font-medium text-text-muted'>Pipeline ativo</p>
          <div className='mt-2'>
            <AnimatedKpiValue
              numericValue={valuesVisible ? pipelineValue : undefined}
              displayValue={
                valuesVisible
                  ? useCompact
                    ? formatCurrencyCompact(pipelineValue)
                    : formatDashboardCurrency(pipelineValue, true)
                  : '••••'
              }
              format='currency'
              delay={animationDelay}
              compactCurrency={useCompact}
              className='text-4xl font-bold tracking-tight text-text-primary sm:text-[2.75rem]'
            />
          </div>
          <p className='mt-2 text-sm text-text-muted'>
            {formatDashboardNumber(pipelineCount, valuesVisible)} negociações abertas
            {pipelineCount > 0 ? (
              <>
                {' '}
                · ticket{' '}
                {valuesVisible
                  ? ticket >= 10_000
                    ? formatCurrencyCompact(ticket)
                    : formatDashboardCurrency(ticket, true)
                  : '••••'}
              </>
            ) : null}
          </p>
        </div>
        <div
          className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl'
          style={{ backgroundColor: accent.bg, color: accent.text }}
        >
          <DollarSign size={22} aria-hidden />
        </div>
      </div>

      {stages.length > 0 ? (
        <div className='mt-auto space-y-3 border-t border-[var(--border)] pt-4'>
          <p className='text-[10px] font-semibold uppercase tracking-wide text-text-muted'>
            Concentração por etapa
          </p>
          <ul className='space-y-2.5'>
            {stages.slice(0, 4).map((step) => {
              const pctOfPipeline =
                pipelineCount > 0 ? Math.round((step.count / pipelineCount) * 100) : 0
              return (
                <li key={step.macro.id} className='space-y-1'>
                  <div className='flex items-center justify-between gap-3 text-xs'>
                    <span className='flex min-w-0 items-center gap-2 font-medium text-text-secondary'>
                      <span
                        className='h-2 w-2 shrink-0 rounded-full'
                        style={{ backgroundColor: step.macro.color }}
                        aria-hidden
                      />
                      <span className='truncate'>{step.macro.label}</span>
                    </span>
                    <span className='shrink-0 tabular-nums text-text-muted'>
                      {formatDashboardNumber(step.count, valuesVisible)} · {pctOfPipeline}%
                    </span>
                  </div>
                  <div className='h-1.5 overflow-hidden rounded-full bg-[var(--bg-dark)]/15'>
                    <div
                      className='h-full rounded-full transition-[width] duration-500'
                      style={{
                        width: `${Math.min(100, pctOfPipeline)}%`,
                        backgroundColor: step.macro.color,
                      }}
                    />
                  </div>
                </li>
              )
            })}
          </ul>
          {outsideFunnel > 0 ? (
            <p className='text-xs text-text-muted'>
              +{formatDashboardNumber(outsideFunnel, valuesVisible)} em fechamento futuro / pausado
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}
